// Partnerships Panel Component
// For configuring reseller/partner proposal details

import { useQuoteStore } from '../store'
import type {
  BillingFrequency,
  TransactionFeeBilling,
  PaymentTermsDays,
  LicensePlanType,
} from '../types'
import {
  Building2,
  FileText,
  Settings,
  Shield,
  Check,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { useState } from 'react'
import { clsx } from 'clsx'

const BILLING_FREQUENCIES: BillingFrequency[] = [
  'One-off payment',
  'Annually in Advance',
  'Quarterly in Advance',
  'Monthly in Arrears',
]

const TRANSACTION_FEE_BILLING_OPTIONS: TransactionFeeBilling[] = [
  'Monthly in arrears once minimum commitment has been fulfilled',
  'Monthly in arrears',
  'Prepaid against minimum commitment',
]

const PAYMENT_TERMS_OPTIONS: PaymentTermsDays[] = [14, 30, 45, 60]

const LICENSE_PLAN_TYPES: LicensePlanType[] = ['BASE', 'PRO', 'ENTERPRISE']

export function PartnershipsPanel() {
  const {
    partnership,
    setIsReseller,
    setPartnershipTerms,
    setImplementationScope,
    setLicensePlan,
    setAnnualLicenseFee,
    quoteLocks,
  } = useQuoteStore()

  // All sections collapsed by default
  const [expandedSections, setExpandedSections] = useState({
    license: false,
    terms: false,
    scope: false,
  })

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  return (
    // <fieldset disabled> disables every form control inside in one go —
    // used to lock the quote while it's under review.
    <fieldset disabled={quoteLocks.all} className="space-y-4 border-0 p-0 m-0 min-w-0">
      {/* Reseller Toggle */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="text-primary" size={20} />
            <div>
              <h3 className="font-medium text-[var(--text-primary)]">Reseller Proposal</h3>
              <p className="text-sm text-[var(--text-muted)]">
                Enable to configure partnership terms for reseller proposals
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsReseller(!partnership.isReseller)}
            className={clsx(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              partnership.isReseller ? 'bg-primary' : 'bg-gray-600'
            )}
          >
            <span
              className={clsx(
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                partnership.isReseller ? 'translate-x-6' : 'translate-x-1'
              )}
            />
          </button>
        </div>
      </div>

      {partnership.isReseller && (
        <>
          {/* License Plan Section - FIRST */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('license')}
              className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              <div className="flex items-center gap-3">
                <Shield className="text-primary" size={18} />
                <span className="font-medium text-[var(--text-primary)]">License Plan</span>
              </div>
              {expandedSections.license ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.license && (
              <div className="px-4 pb-4">
                {/* Plan selector */}
                <div className="flex gap-2 mb-4">
                  {LICENSE_PLAN_TYPES.map((planType) => (
                    <button
                      key={planType}
                      onClick={() => setLicensePlan(planType)}
                      className={clsx(
                        'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors',
                        partnership.licensePlan.type === planType
                          ? 'bg-primary text-white'
                          : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]/80'
                      )}
                    >
                      {planType}
                    </button>
                  ))}
                </div>

                {/* Plan features */}
                <div className="bg-[var(--bg-tertiary)] rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--text-muted)]">Rulesets</span>
                    <span className="text-[var(--text-primary)]">
                      {partnership.licensePlan.features.rulesets}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--text-muted)]">Portal Seats</span>
                    <span className="text-[var(--text-primary)]">
                      {partnership.licensePlan.features.portalSeats}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--text-muted)]">UAT/Sandbox Accounts</span>
                    <span className="text-[var(--text-primary)]">
                      {partnership.licensePlan.features.uatSandboxAccounts}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--text-muted)]">Child Accounts</span>
                    <span className="text-[var(--text-primary)]">
                      {partnership.licensePlan.features.childAccounts}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--text-muted)]">Evidence Capture</span>
                    <span className="text-[var(--text-primary)]">
                      {partnership.licensePlan.features.evidenceCapture ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <X size={16} className="text-red-500" />
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--text-muted)]">API Access</span>
                    <span className="text-[var(--text-primary)]">
                      {partnership.licensePlan.features.apiAccess ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <X size={16} className="text-red-500" />
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--text-muted)]">Basic Hosted OneSDK</span>
                    <span className="text-[var(--text-primary)]">
                      {partnership.licensePlan.features.basicHostedOneSDK ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <X size={16} className="text-red-500" />
                      )}
                    </span>
                  </div>
                </div>

                {/* Annual license fee */}
                <div className="mt-4">
                  <label className="block text-sm text-[var(--text-muted)] mb-1">
                    Annual License Fee
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">
                      $
                    </span>
                    <input
                      type="number"
                      value={partnership.annualLicenseFee || ''}
                      onChange={(e) => setAnnualLicenseFee(parseFloat(e.target.value) || 0)}
                      className="w-full pl-7 pr-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)]"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Billing Terms Section */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('terms')}
              className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="text-primary" size={18} />
                <span className="font-medium text-[var(--text-primary)]">Billing Terms</span>
              </div>
              {expandedSections.terms ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.terms && (
              <div className="px-4 pb-4 space-y-3">
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">
                    Implementation Fee Billing
                  </label>
                  <select
                    value={partnership.terms.implementationFeeBilling}
                    onChange={(e) =>
                      setPartnershipTerms({ implementationFeeBilling: e.target.value as BillingFrequency })
                    }
                    className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)]"
                  >
                    {BILLING_FREQUENCIES.map((freq) => (
                      <option key={freq} value={freq}>
                        {freq}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">
                    Licence Fee Billing PA
                  </label>
                  <select
                    value={partnership.terms.licenceFeeBilling}
                    onChange={(e) =>
                      setPartnershipTerms({ licenceFeeBilling: e.target.value as BillingFrequency })
                    }
                    className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)]"
                  >
                    {BILLING_FREQUENCIES.map((freq) => (
                      <option key={freq} value={freq}>
                        {freq}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">
                    Minimum Commitment Billing PA
                  </label>
                  <select
                    value={partnership.terms.minimumCommitmentBilling}
                    onChange={(e) =>
                      setPartnershipTerms({ minimumCommitmentBilling: e.target.value as BillingFrequency })
                    }
                    className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)]"
                  >
                    {BILLING_FREQUENCIES.map((freq) => (
                      <option key={freq} value={freq}>
                        {freq}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">
                    Transaction Fee Billing
                  </label>
                  <select
                    value={partnership.terms.transactionFeeBilling}
                    onChange={(e) =>
                      setPartnershipTerms({ transactionFeeBilling: e.target.value as TransactionFeeBilling })
                    }
                    className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)]"
                  >
                    {TRANSACTION_FEE_BILLING_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">Payment Terms</label>
                  <select
                    value={partnership.terms.paymentTermsDays}
                    onChange={(e) =>
                      setPartnershipTerms({ paymentTermsDays: parseInt(e.target.value) as PaymentTermsDays })
                    }
                    className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-md text-sm text-[var(--text-primary)]"
                  >
                    {PAYMENT_TERMS_OPTIONS.map((days) => (
                      <option key={days} value={days}>
                        {days} days upon invoice
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Implementation Scope Section */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('scope')}
              className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              <div className="flex items-center gap-3">
                <Settings className="text-primary" size={18} />
                <span className="font-medium text-[var(--text-primary)]">Implementation Scope</span>
              </div>
              {expandedSections.scope ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.scope && (
              <div className="px-4 pb-4 space-y-2">
                {/* Checkboxes for scope items */}
                <ScopeCheckbox
                  label="FrankieOne Portal"
                  checked={partnership.implementationScope.frankieOnePortal}
                  onChange={(v) => setImplementationScope({ frankieOnePortal: v })}
                />
                <ScopeCheckbox
                  label="SDK Recommendations"
                  checked={partnership.implementationScope.sdkRecommendations}
                  onChange={(v) => setImplementationScope({ sdkRecommendations: v })}
                />
                <ScopeCheckbox
                  label="API Integration Support & Architecture Guidance"
                  checked={partnership.implementationScope.apiIntegrationSupport}
                  onChange={(v) => setImplementationScope({ apiIntegrationSupport: v })}
                />
                <ScopeCheckbox
                  label="SME KYC, IDV, KYB Recipes"
                  checked={partnership.implementationScope.smeKycIdvKybRecipes}
                  onChange={(v) => setImplementationScope({ smeKycIdvKybRecipes: v })}
                />
                <ScopeCheckbox
                  label="Standard Recipes/Workflows from 4 out of the box templates"
                  checked={partnership.implementationScope.standardRecipesWorkflows}
                  onChange={(v) => setImplementationScope({ standardRecipesWorkflows: v })}
                />
                <ScopeCheckbox
                  label="Service Advice Selection from Partner Ecosystem"
                  checked={partnership.implementationScope.serviceAdviceSelection}
                  onChange={(v) => setImplementationScope({ serviceAdviceSelection: v })}
                />

                {/* Child Account Clone with count */}
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setImplementationScope({
                          childAccountClone: !partnership.implementationScope.childAccountClone,
                        })
                      }
                      className={clsx(
                        'w-5 h-5 rounded flex items-center justify-center transition-colors',
                        partnership.implementationScope.childAccountClone
                          ? 'bg-primary text-white'
                          : 'bg-[var(--bg-tertiary)] border border-[var(--border-color)]'
                      )}
                    >
                      {partnership.implementationScope.childAccountClone && <Check size={14} />}
                    </button>
                    <span className="text-sm text-[var(--text-primary)]">
                      Standard child clone from parent Child Accounts
                    </span>
                  </div>
                  <input
                    type="number"
                    min={1}
                    value={partnership.implementationScope.childAccountCloneCount || ''}
                    onChange={(e) =>
                      setImplementationScope({ childAccountCloneCount: parseInt(e.target.value) || 1 })
                    }
                    className="w-16 px-2 py-1 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-sm text-center"
                  />
                </div>

                {/* Hyper Care with days/weeks */}
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setImplementationScope({
                          hyperCareMonitoring: !partnership.implementationScope.hyperCareMonitoring,
                        })
                      }
                      className={clsx(
                        'w-5 h-5 rounded flex items-center justify-center transition-colors',
                        partnership.implementationScope.hyperCareMonitoring
                          ? 'bg-primary text-white'
                          : 'bg-[var(--bg-tertiary)] border border-[var(--border-color)]'
                      )}
                    >
                      {partnership.implementationScope.hyperCareMonitoring && <Check size={14} />}
                    </button>
                    <span className="text-sm text-[var(--text-primary)]">
                      Hyper care and performance monitoring after Go Live
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      value={partnership.implementationScope.hyperCareDays || ''}
                      onChange={(e) =>
                        setImplementationScope({ hyperCareDays: parseInt(e.target.value) || 1 })
                      }
                      className="w-12 px-2 py-1 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-sm text-center"
                    />
                    <span className="text-xs text-[var(--text-muted)]">days over</span>
                    <input
                      type="number"
                      min={1}
                      value={partnership.implementationScope.hyperCareWeeks || ''}
                      onChange={(e) =>
                        setImplementationScope({ hyperCareWeeks: parseInt(e.target.value) || 1 })
                      }
                      className="w-12 px-2 py-1 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-sm text-center"
                    />
                    <span className="text-xs text-[var(--text-muted)]">weeks</span>
                  </div>
                </div>

                <ScopeCheckbox
                  label="Portal Training (Train the trainer)"
                  checked={partnership.implementationScope.portalTraining}
                  onChange={(v) => setImplementationScope({ portalTraining: v })}
                />
              </div>
            )}
          </div>
        </>
      )}
    </fieldset>
  )
}

// Helper component for scope checkboxes
function ScopeCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-center gap-2 py-2">
      <button
        onClick={() => onChange(!checked)}
        className={clsx(
          'w-5 h-5 rounded flex items-center justify-center transition-colors',
          checked
            ? 'bg-primary text-white'
            : 'bg-[var(--bg-tertiary)] border border-[var(--border-color)]'
        )}
      >
        {checked && <Check size={14} />}
      </button>
      <span className="text-sm text-[var(--text-primary)]">{label}</span>
    </div>
  )
}
